import { useState, useEffect, useRef } from 'react';
import { APIRoutes } from '@/services/api.routes';
import type { DayTradingSessionID } from '@/models/ids';

const DEFAULT_POLL_INTERVAL_MS = 10_000;

export type DirectStreamStatus = 'live' | 'offline' | 'error' | 'unknown';

interface UseDirectStreamStatusReturn {
  status: DirectStreamStatus;
}

/**
 * Polls the backend's MediaMTX live status for a session. Returns 'unknown'
 * until the first poll completes, then 'live'/'offline' as MediaMTX reports,
 * or 'error' if the upstream call fails.
 *
 * Polling stops automatically when `enabled` becomes false (e.g. session is
 * not in DIRECT mode), so we don't hammer the backend for sessions that don't
 * have a direct stream.
 */
export function useDirectStreamStatus(
  sessionId: DayTradingSessionID,
  enabled: boolean,
  pollingIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
): UseDirectStreamStatusReturn {
  const [status, setStatus] = useState<DirectStreamStatus>('unknown');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('unknown');
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const poll = async () => {
      try {
        const result = await APIRoutes.getDayTradingSessionStreamStatus(sessionId);
        if (cancelled || !mountedRef.current) return;
        setStatus(result.live ? 'live' : 'offline');
      } catch {
        if (cancelled || !mountedRef.current) return;
        setStatus('error');
      }
    };

    poll();
    intervalId = setInterval(poll, pollingIntervalMs);

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [sessionId, enabled, pollingIntervalMs]);

  return { status };
}
